import { SelectChangeEvent, Drawer, Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import * as uuid from 'uuid';
import BkChipAutocomplete from 'src/components/autocomplete/BkChipAutocomplete';
import { CompanyInfo } from 'src/features/company/constants';
import { ModalTitle } from 'src/components/new/modal/ModalTitle';
import { ModalContent } from 'src/components/new/modal/ModalContent';
import { ModalFooter } from 'src/components/new/modal/ModalFooter';
import { TextField } from 'src/components/new/inputs/TextField';
import SettingsSelect from 'src/components/select/SettingsSelect';
import { Typography } from 'src/components/new/Typography';
import { countryPhoneInfo, stateMapper } from 'src/utils/constant';
import { checkForInvalidDomains, isValidZipCode } from 'src/utils/utility-functions';
import Select from 'src/components/new/inputs/Select';
import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js';
import { format, useMask } from '@react-input/mask';

interface EditCompanyDataProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (companyInfo: CompanyInfo) => void;
  info: CompanyInfo;
}

interface CompanyDataErrors {
  nameError: string;
  companyPhoneNumberError: string;
  addressOneError: string;
  addressTwoError: string;
  websiteUrlError: string;
  cityError: string;
  stateError: string;
  zipCodeError: string;
  domainsError: string;
}

const EditCompanyData: React.FC<EditCompanyDataProps> = ({ open, onClose, onSubmit, info }) => {
  const [newCompanyInfo, setNewCompanyInfo] = useState<CompanyInfo>({
    name: '',
    addressOne: '',
    addressTwo: '',
    city: '',
    state: '',
    countryCode: 'US',
    zipCode: '',
    companyPhoneNumber: '',
    websiteUrl: '',
    domains: [],
    id: null,
  });

  const [errors, setErrors] = useState<CompanyDataErrors>({
    nameError: '',
    companyPhoneNumberError: '',
    addressOneError: '',
    addressTwoError: '',
    websiteUrlError: '',
    cityError: '',
    stateError: '',
    zipCodeError: '',
    domainsError: '',
  });

  const onStateChange = (event: SelectChangeEvent<string>) => {
    setNewCompanyInfo((prevState) => ({
      ...prevState,
      state: event.target.value,
    }));
  };

  const onCountryCodeChange = (event: SelectChangeEvent<string>) => {
    setNewCompanyInfo((prevState) => ({
      ...prevState,
      countryCode: event.target.value as CountryCode,
    }));
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setNewCompanyInfo((prevState) => ({ ...prevState, [name]: value }));
  };

  const inputRef = useMask({
    mask: countryPhoneInfo[newCompanyInfo.countryCode as keyof typeof countryPhoneInfo].mask,
    replacement: { _: /\d/ },
    showMask: true,
  });
  const setTags = (type: string, tags: string[]) => {
    const updatedDomains = tags.map((tag) => {
      const normalizedTag = tag.replace(/^@+/g, '').toLowerCase();
      const existingTag = info.domains.find((domain) => domain.name === normalizedTag);
      return existingTag ?? { id: uuid.v4(), name: normalizedTag };
    }).filter((domain, index, self) => self.findIndex(d => d.name === domain.name) === index);

    setNewCompanyInfo((prevState) => ({
      ...prevState,
      domains: updatedDomains,
    }));
  };

  useEffect(() => {
    if (open && info !== undefined) {
      setNewCompanyInfo(info);
    }

    return () => {
      if (!open) {
        setNewCompanyInfo({
          name: '',
          addressOne: '',
          addressTwo: '',
          city: '',
          state: '',
          countryCode: 'US',
          zipCode: '',
          companyPhoneNumber: '',
          websiteUrl: '',
          domains: [],
          id: null,
        });

        setErrors({
          nameError: '',
          companyPhoneNumberError: '',
          addressOneError: '',
          addressTwoError: '',
          websiteUrlError: '',
          cityError: '',
          stateError: '',
          zipCodeError: '',
          domainsError: '',
        });
      }
    };
  }, [open]);

  const handleSubmit = () => {
    const urlRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,}){1,2}?$/;
    const newErrors: CompanyDataErrors = {
      nameError: '',
      companyPhoneNumberError: '',
      addressOneError: '',
      addressTwoError: '',
      websiteUrlError: '',
      cityError: '',
      stateError: '',
      zipCodeError: '',
      domainsError: '',
    };

    if (!newCompanyInfo.name) {
      newErrors.nameError = 'Company Name cannot be empty!';
    }
    if (!newCompanyInfo.addressOne) {
      newErrors.addressOneError = 'Address cannot be empty!';
    }
    if (!newCompanyInfo.companyPhoneNumber) {
      newErrors.companyPhoneNumberError = 'Phone Number cannot be empty!';
    }
    if (newCompanyInfo.websiteUrl && !urlRegex.test(newCompanyInfo.websiteUrl)) {
      newErrors.websiteUrlError = 'Invalid Website URL';
    }
    const cellPhone = parsePhoneNumberFromString(newCompanyInfo.companyPhoneNumber, newCompanyInfo.countryCode);

    if (newCompanyInfo.companyPhoneNumber && !cellPhone?.isValid()) {
      newErrors.companyPhoneNumberError = 'Wrong phone number format';
    }

    if (!newCompanyInfo.city) {
      newErrors.cityError = 'City cannot be empty!';
    }
    if (!newCompanyInfo.state) {
      newErrors.stateError = 'State cannot be empty!';
    }
    if (!newCompanyInfo.zipCode) {
      newErrors.zipCodeError = 'Zip code cannot be empty!';
    }
    if (
      newCompanyInfo.countryCode === 'US' &&
      newCompanyInfo.state &&
      newCompanyInfo.zipCode &&
      !isValidZipCode(newCompanyInfo.state, newCompanyInfo.zipCode)
    ) {
      newErrors.zipCodeError = 'Zip Code is Invalid';
    }

    const invalidDomains = checkForInvalidDomains(newCompanyInfo.domains);

    if (invalidDomains.length > 0) {
      newErrors.domainsError = `The following domains are invalid: ${invalidDomains.join(', ')}`;
    }

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error !== '');

    if (!hasErrors) {
      onSubmit({
        ...newCompanyInfo,
        companyPhoneNumber: newCompanyInfo.companyPhoneNumber.replace(/\D/g, ''),
      });
    }
  };

  useEffect(() => {
    const mask = countryPhoneInfo[newCompanyInfo.countryCode as keyof typeof countryPhoneInfo].mask;
    let phoneDigits = newCompanyInfo.companyPhoneNumber.replace(/\D/g, '');
    setNewCompanyInfo((prevState) => ({
      ...prevState,
      companyPhoneNumber: format(phoneDigits, { mask, replacement: { _: /\d/ } }),
    }));
  }, [newCompanyInfo.countryCode]);

  return (
    <Drawer
      open={open}
      anchor="right"
      onClose={onClose}
      sx={{
        '& .MuiPaper-root': {
          background: '#FFF',
          width: '30%',
        },
      }}
    >
      <ModalTitle>Edit Company Profile</ModalTitle>
      <ModalContent>
        <Box style={{ width: '100%' }}>
          <TextField
            id="name"
            label="Company Name"
            value={newCompanyInfo.name}
            name="name"
            inputProps={{ maxLength: 60 }}
            required
            error={errors.nameError}
            onChange={onChange}
          />
        </Box>
        <Box
          style={{
            width: '100%',
            display: 'flex',
            gap: '0.625rem',
          }}
        >
          <Select
            containerSx={{ width: '30%' }}
            label="Country Code"
            required
            data={Object.entries(countryPhoneInfo).map(([id, { code }]) => ({
              id,
              label: `${id} (${code})`,
            }))}
            onChange={onCountryCodeChange}
            value={newCompanyInfo.countryCode}
            disabled={false}
            error=""
          />
          <TextField
            inputRef={inputRef}
            containerSX={{ width: '75%' }}
            id="companyPhoneNumber"
            label="Company Phone Number"
            required
            value={newCompanyInfo.companyPhoneNumber}
            name="companyPhoneNumber"
            error={errors.companyPhoneNumberError}
            onChange={onChange}
          />
        </Box>
        <Box style={{ width: '100%' }}>
          <TextField
            id="addressOne"
            label="Address 1"
            value={newCompanyInfo.addressOne}
            name="addressOne"
            required
            error={errors.addressOneError}
            onChange={onChange}
          />
        </Box>
        <Box style={{ width: '100%' }}>
          <TextField
            id="addressTwo"
            label="Address 2"
            value={newCompanyInfo.addressTwo}
            name="addressTwo"
            error={errors.addressTwoError}
            onChange={onChange}
          />
        </Box>
        <Box
          style={{
            display: 'flex',
            gap: '0.625rem',
            width: '100%',
          }}
        >
          <TextField
            id="city"
            label="City"
            value={newCompanyInfo.city}
            name="city"
            error={errors.cityError}
            onChange={onChange}
            required
          />
          <Box style={{ width: '100%', display: 'flex', gap: '0.625rem' }}>
            {newCompanyInfo.countryCode === 'US' ? (
              <SettingsSelect
                label="State"
                value={newCompanyInfo.state}
                error={errors.stateError}
                required
                data={Object.values(stateMapper)}
                disabled={false}
                onChange={onStateChange}
              />
            ) : (
              <TextField
                label="State"
                required
                value={newCompanyInfo.state}
                error={errors.stateError}
                onChange={onStateChange}
                id="state"
                name="state"
              />
            )}
            <TextField
              id="zipCode"
              label={newCompanyInfo.countryCode === 'CA' ? 'Postal code' : 'ZIP'}
              value={newCompanyInfo.zipCode}
              required
              name="zipCode"
              error={errors.zipCodeError}
              onChange={onChange}
            />
          </Box>
        </Box>
        <Box style={{ width: '100%' }}>
          <TextField
            id="websiteUrl"
            label="Website"
            value={newCompanyInfo.websiteUrl}
            name="websiteUrl"
            error={errors.websiteUrlError}
            onChange={onChange}
          />
        </Box>
        <Box style={{ width: '100%' }}>
          <Typography sx={{ fontWeight: '500', color: '#1D1B20', fontSize: '0.875rem', lineHeight: '1.25rem' }}>
            Add Associated Domains
          </Typography>
          <BkChipAutocomplete
            title="Organization Domains"
            tags={newCompanyInfo.domains.map((el) => el.name)}
            setTags={setTags}
            errorOnSubmit={''}
            type="organizationDomains"
            error={errors.domainsError}
          />
        </Box>
      </ModalContent>
      <ModalFooter onClose={onClose} onSubmit={handleSubmit} closeText="Cancel" submitText="Save" />
    </Drawer>
  );
};

export default EditCompanyData;
